/**
 * eventPermissions — compute the full per-button permission flag map for an
 * event in Calendar Month V2's `EventViewModal`. Port of the legacy AngularJS
 * gating logic at `calendar.event-modal-footer.js:106-343` +
 * `calendar.event-view.js:266-338`. No simplifications.
 *
 * Why this exists: prior to Phase 2.2, `EventViewModal` was gating buttons
 * with ad-hoc `perms.canDelete` checks that did NOT match the legacy
 * formulas. Real bug: Cancel was wired to `canDelete` instead of
 * `availableToManage`, causing 403s on `/calendar/event/{id}/cancel` for
 * tester `aartemorlov1998@holdshort.com` who had Cancel but not Delete
 * perms. This util is the single source of truth; consumers just read
 * the returned flag map.
 *
 * Backend authority (server-side enforcement): `EventValidatorTrait.php:24-34`
 * — maintenance requires BOTH `resourcesManage` AND `maintenanceManage`.
 * The Trait is the ultimate gate; this util mirrors it so the UI hides
 * buttons that would 403 anyway.
 *
 * @module eventPermissions
 */

import { normalizeAccessGroups } from './normalizeAccessGroups.js';

/**
 * Whether the current user owns the event (legacy `isPilot`).
 *
 * Legacy field name is `event.pilotID` (capital ID). Some normalized
 * shapes use `pilotId` (camelCase). Maintenance events have no pilot; the
 * fallback to `payPersonID` mirrors the legacy `calendar.event-view.js`
 * which falls back to the financial-responsible user.
 *
 * @param {Object} event
 * @param {number|string|null} currentUserId
 * @returns {boolean}
 */
export function isOwnEvent(event, currentUserId) {
	if (!event || currentUserId == null) return false;
	const pilot =
		event.pilotID ??
		event.pilotId ??
		event.payPersonID ??
		event.payPersonId ??
		null;
	if (pilot == null) return false;
	return String(pilot) === String(currentUserId);
}

/**
 * `true` when the current user is the instructor assigned to the event
 * (legacy parity for `isUserEventInstructor` at
 * `calendar/modals/utils/permissions.js:1-5`). The instructor field on an
 * event is a *resource* id, NOT a user id — look up the resource in the
 * map and compare its underlying user id field.
 *
 * Backend ships the user-id on the resource under a few different names
 * depending on response shape (`userID` / `userId` / `_userID` /
 * `instructorId`); accept any.
 *
 * @param {Object} event
 * @param {number|string|null} currentUserId
 * @param {Object|null} resourcesMap  Redux `state.resources` map keyed by resource id.
 * @returns {boolean}
 */
export function isInstructorUser(event, currentUserId, resourcesMap) {
	if (!event || currentUserId == null) return false;
	if (!resourcesMap || typeof resourcesMap !== 'object') return false;
	const instructorResourceId =
		event.instructorID ?? event.instructorId ?? null;
	if (instructorResourceId == null) return false;
	const resource = resourcesMap[instructorResourceId] ?? resourcesMap[String(instructorResourceId)];
	if (!resource) return false;
	const resourceUserId =
		resource.userID ??
		resource.userId ??
		resource._userID ??
		resource.instructorId ??
		null;
	if (resourceUserId == null) return false;
	return String(resourceUserId) === String(currentUserId);
}

/**
 * `true` when the current user owns the event, for consumers that receive
 * the EventViewModal **detail-modal** shape (`event.pilotId`,
 * `event.instructor.id` — both USER ids) rather than the month-grid shape
 * that `useCalendarPermissions.isOwner` reads (`pilotID`/`instructorID`,
 * where `instructorID` is a RESOURCE id). Comparing a modal event against
 * that grid-only helper would treat every owner as a non-owner.
 *
 * The nested `instructor` object only exists on the detail/modal shape —
 * its presence is what selects the user-id comparison. Without it, this
 * falls back to the resource-id lookup (`isInstructorUser`) so the helper
 * also works against grid-shape events.
 *
 * @param {Object} event
 * @param {number|string|null} currentUserId
 * @param {Object|null} resourcesMap Redux `state.resources`, used only for
 *   the grid-shape instructor fallback.
 * @returns {boolean}
 */
export function isWbEventOwner(event, currentUserId, resourcesMap = null) {
	if (!event || currentUserId == null) return false;
	const sameAs = id => id != null && String(id) === String(currentUserId);

	const pilotId = event.pilotId ?? event.pilotID ?? null;
	if (sameAs(pilotId)) return true;

	const hasInstructorObject = event.instructor && typeof event.instructor === 'object';
	if (hasInstructorObject) {
		return sameAs(event.instructor.id ?? event.instructorId ?? null);
	}

	return isInstructorUser(event, currentUserId, resourcesMap);
}

/**
 * `true` if the event is a maintenance event. Symfony uses lowercase
 * `'maintenance'`; legacy AngularJS sometimes ships `'Maintenance'`.
 * Normalized comparison covers both.
 *
 * @param {Object} event
 * @returns {boolean}
 */
export function isMaintenance(event) {
	const type = event?.type;
	if (typeof type !== 'string') return false;
	return type.toLowerCase().includes('maint');
}

/**
 * Resolve the per-org `accessGroups` object for the event's org.
 *
 * `accessGroups` here is the cross-org map keyed by `orgUnique` (as
 * provided by the bootstrap fan-out at `bootstrapSlice.js:297-330`).
 * Returns `{}` when the lookup misses — caller will short-circuit every
 * flag to false naturally because every flag reads a truthy property
 * from this object.
 *
 * Runtime shape (verified 2026-05-15 via React DevTools): the bootstrap
 * stores `accessGroups` as a **41-item array of permission name strings**
 * (e.g. `["dashboard", "calendarManageMy", ...]`). `computeEventPermissions`
 * reads it as a boolean map (`org.calendarManageMy`), so we normalize
 * array → `{name: true}` here. Object inputs pass through untouched.
 *
 * @param {Object} event
 * @param {Object|Array|null} accessGroups  Map keyed by orgUnique → flags object/array.
 * @returns {Object}
 */
export function getOrgPerms(event, accessGroups) {
	if (!accessGroups || typeof accessGroups !== 'object') return {};
	const orgUnique = event?.organizationUnique || event?.orgUnique || null;
	if (!orgUnique) return {};
	const entry = accessGroups[orgUnique];
	if (entry == null) return {};
	// Bootstrap may store the flags directly OR under `.accessGroups` (legacy
	// AngularJS shape). Accept either. Direct array shape is the live runtime
	// shape — `normalizeAccessGroups` handles array → object conversion.
	const raw = (entry && typeof entry === 'object' && !Array.isArray(entry) && entry.accessGroups)
		? entry.accessGroups
		: entry;
	return normalizeAccessGroups(raw);
}

/**
 * `true` when the current user holds `weightBalanceEventManage` in the
 * EVENT's own organization — the gate for the W&B manager route. Reads
 * through the same cross-org `accessGroups` map as `computeEventPermissions`
 * (current-org bootstrap flags + the Phase-3 fan-out via `getOrgPerms`), NOT
 * a page-level `serverData` flag, so a multi-org calendar keeps resolving
 * the right org. `weightBalanceEventManage` needs no entry in
 * `normalizeAccessGroups` — every held permission name already passes
 * through as `{ name: true }`.
 *
 * This decides the route only — never whether the write is allowed at all.
 * An owner without this permission still writes, through the ownership-
 * scoped `update-my` route (see `isWbEventOwner`); the backend re-checks
 * ownership on that route, so a wrong client-side guess can't write the
 * wrong event.
 *
 * @param {Object} event
 * @param {Object|Array|null} accessGroups Cross-org map (see `getOrgPerms`).
 * @returns {boolean}
 */
export function canManageEventWeightBalance(event, accessGroups) {
	return !!getOrgPerms(event, accessGroups).weightBalanceEventManage;
}

/**
 * Compute the per-button permission flag map.
 *
 * @param {Object} event
 * @param {Object|null} accessGroups  Cross-org map (see getOrgPerms).
 * @param {number|string|null} currentUserId
 * @returns {{
 *   canEdit: boolean,
 *   canCancel: boolean,
 *   canDelete: boolean,
 *   canCancelCheckOut: boolean,
 *   canCancelCheckIn: boolean,
 *   canCheckOut: boolean,
 *   canCheckIn: boolean,
 *   canCreateInvoice: boolean,
 *   canViewInvoice: boolean,
 *   canAddToWaiting: boolean
 * }}
 */
export function computeEventPermissions(event, accessGroups, currentUserId, resourcesMap = null) {
	const empty = {
		canEdit: false,
		canCancel: false,
		canDelete: false,
		canCancelCheckOut: false,
		canCancelCheckIn: false,
		canCheckOut: false,
		canCheckIn: false,
		canCreateInvoice: false,
		canViewInvoice: false,
		canAddToWaiting: false
	};
	if (!event) return empty;

	const org = getOrgPerms(event, accessGroups);
	const isOwn = isOwnEvent(event, currentUserId);
	// Rem18 Task 2 — instructor-as-pilot parity. Legacy
	// `calendar.event-modal-footer.js:117-128` treats an instructor
	// assigned to the event as ownership-equivalent. We require
	// `calendarManageMy` (NOT a free pass), so an instructor with their
	// own manage-my perm gets the same gates as a pilot owner.
	const isInstructor = isInstructorUser(event, currentUserId, resourcesMap);
	const isMaint = isMaintenance(event);
	const isAir = !isMaint;

	// Per-permission flags (legacy parity). Falsy/missing → false.
	const calendarManageMy = !!org.calendarManageMy;
	const calendarManageAnothers = !!org.calendarManageAnothers;
	const calendarEventDelete = !!org.calendarEventDelete;
	const maintenanceManage = !!org.maintenanceManage;
	const resourcesManage = !!org.resourcesManage;
	const checkOutAircraft = !!org.checkOutAircraft;
	const invoiceCreate = !!org.invoiceCreate || !!org.productsView;

	// Legacy compound flags (mirror `calendar.event-modal-footer.js:119-128`).
	// Instructor branch (Rem18 Task 2): treated as owner for permission
	// purposes — uses `calendarManageMy`, NOT `calendarManageAnothers`.
	const isOwnOrInstructor = isOwn || isInstructor;
	const canManageAir = isAir && (isOwnOrInstructor ? calendarManageMy : calendarManageAnothers);
	// Backend `EventValidatorTrait.php:24-34` — maintenance requires BOTH.
	const canManageMaint = isMaint && resourcesManage && maintenanceManage;
	const availableToManage = canManageAir || canManageMaint;

	// Event lifecycle state (legacy reads from `event.additionalData` /
	// `event.checkOutEnable` etc; both shapes accepted).
	const checkOutEnable = Number(event.checkOutEnable ?? event.additionalData?.checkOutEnable ?? 0);
	const checkInEnable = Number(event.checkInEnable ?? event.additionalData?.checkInEnable ?? 0);
	// Rem20 Sub 7b — Symfony `EventAdditionalDataDto.php:31` ships
	// `aviationResourceExists` (current name); older response shapes use
	// `aircraftExists` (legacy name). Accept BOTH at top level AND nested
	// under `additionalData` so the `canCheckOut` / `canCheckIn` /
	// `canAddToWaiting` gates fire correctly. Symfony serializes as
	// boolean — `Number(true)` → 1, `Number(false)` → 0 (matches the
	// `=== 1` comparisons below).
	const aircraftExistsRaw =
		event.aviationResourceExists
		?? event.additionalData?.aviationResourceExists
		?? event.aircraftExists
		?? event.additionalData?.aircraftExists
		?? 0;
	const aircraftExists = Number(aircraftExistsRaw === true ? 1 : aircraftExistsRaw === false ? 0 : aircraftExistsRaw);
	const checkOutTime = Number(event.checkOutTime ?? 0);
	const checkInTime = Number(event.checkInTime ?? 0);
	const eventCompleted = checkOutEnable === 0 && checkInEnable === 0 && aircraftExists === 1;

	// Invoice state.
	const invoiceId = event.invoiceId || event.invoiceID || null;

	// Per legacy: Edit also needs `event.availableForEdit === 1` for non-recurring
	// cancellation/completion guards. Accept boolean or string '1'.
	const availableForEditField = event.availableForEdit;
	const availableForEdit = availableForEditField === undefined || availableForEditField === null
		? true // legacy default when field absent (e.g. fresh in-memory event)
		: (availableForEditField === 1 || availableForEditField === '1' || availableForEditField === true);

	return {
		// Edit:
		//   air: own → calendarManageMy; other → calendarManageAnothers
		//   maint: resourcesManage AND maintenanceManage
		canEdit: availableToManage && availableForEdit,

		// Cancel (NOT delete):
		//   Legacy formula: `availableToManage && isAirEvent && !eventCompleted`
		//   (`calendar.event-modal-footer.js:289`). FIX for prior bug at
		//   `EventViewModal.js:1134-1139` which used `canDelete` instead.
		canCancel: availableToManage && isAir && !eventCompleted,

		// Delete:
		//   Distinct `calendarEventDelete` perm (NOT compound with manage).
		//   Maintenance: also accepts `maintenanceManage` per legacy
		//   `calendar.event-modal-footer.js:209-211`.
		canDelete:
			((calendarEventDelete && (isAir || isMaint)) || (isMaint && canManageMaint)) &&
			!eventCompleted,

		// Cancel Check Out:
		//   `availableToManage && checkOutEnable === 0 && checkInEnable === 1
		//    && aircraftExists === 1` — i.e. user already started checkout.
		canCancelCheckOut:
			availableToManage &&
			checkOutEnable === 0 &&
			checkInEnable === 1 &&
			aircraftExists === 1,

		// Cancel Check In:
		//   `checkOutAircraft && eventCompleted` — event finished, user wants
		//   to revert. Legacy: `accessgroups.checkOutAircraft ? eventCompleted ? <button>`.
		canCancelCheckIn: checkOutAircraft && eventCompleted,

		// Check Out (Status block start):
		//   accessgroups.checkOutAircraft + per-org manage + checkOut allowed.
		canCheckOut:
			checkOutAircraft &&
			availableToManage &&
			checkOutEnable === 1 &&
			aircraftExists === 1,

		// Check In (Status block start):
		//   Same gate as Check Out + must currently be in-flight.
		canCheckIn:
			checkOutAircraft &&
			availableToManage &&
			checkOutEnable === 0 &&
			checkInEnable === 1 &&
			aircraftExists === 1,

		// Create Invoice:
		//   own  → invoiceCreate
		//   other → invoiceCreate AND calendarManageAnothers
		//   Only when no invoice already exists and event is air.
		canCreateInvoice:
			isAir &&
			!invoiceId &&
			invoiceCreate &&
			(isOwnOrInstructor || calendarManageAnothers),

		// View Invoice:
		//   Has the invoice perm OR an invoice id already exists (admin links
		//   on prior-created invoices remain visible to involved users).
		canViewInvoice: !!invoiceId && (invoiceCreate || isOwnOrInstructor),

		// Add to Waiting List:
		//   `availableToManage && isAir && aircraftExists === 1 && !eventCompleted`
		canAddToWaiting:
			availableToManage && isAir && aircraftExists === 1 && !eventCompleted
	};
}
